// Analytics and tracking.

import { isLikelyBotUserAgent } from "./service";

// Enable GA in development for testing
const GA_DEBUG = false; // Set to true to enable GA in development

const isGAEnabled = () => {
  return (process.env.REACT_APP_ENVIRONMENT === "production" || GA_DEBUG) &&
         window.location.hostname !== "internal.boxs.in";
};

const isZerobuildHost = () => {
  const host = String(window.location.hostname || "").toLowerCase();
  return host === "zerobuild.in" || host === "www.zerobuild.in";
};

const isPixelEnabled = () => {
  return process.env.REACT_APP_ENVIRONMENT === "production" && isZerobuildHost();
};

// Get the appropriate GA ID based on subdomain
const getGAId = () => {
  return window.location.hostname === "store.boxs.in" ?
    process.env.REACT_APP_STORE_GOOGLE_ANALYTICS_ID :
    process.env.REACT_APP_GOOGLE_ANALYTICS_ID;
};

export const initGA = () => {
  if (isLikelyBotUserAgent()) return;

  if (isGAEnabled()) {
    const gaId = getGAId();

    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
    document.head.appendChild(script);

    window.dataLayer = window.dataLayer || [];
    function gtag() {
      window.dataLayer.push(arguments);
    }
    window.gtag = gtag;

    gtag("js", new Date());

    // Enhanced measurement configuration for cross-domain tracking
    gtag("config", gaId, {
      // Enable enhanced measurement features
      page_title: true,
      page_location: true,

      // Cross-domain configuration
      allow_google_signals: true,
      allow_ad_personalization_signals: true,

      // Enhanced measurement settings
      send_page_view: true,
      anonymize_ip: false,

      // Debug mode for development
      debug_mode: GA_DEBUG,

      // Custom dimensions for ecommerce
      custom_map: {
        'custom_parameter_1': 'product_category',
        'custom_parameter_2': 'user_type'
      }
    });

    // Log GA initialization for development
    if (GA_DEBUG) {
      console.log('🔍 Google Analytics initialized in DEBUG mode');
      console.log('📊 GA ID:', gaId);
      console.log('🌐 Environment:', process.env.REACT_APP_ENVIRONMENT || 'development');
      console.log('🏪 Store GA ID:', process.env.REACT_APP_STORE_GOOGLE_ANALYTICS_ID);
    }
  }
};

export const trackGAEvent = (event, params = null) => {
  if (isGAEnabled() && window.gtag) {
    window.gtag('event', event, params);

    // Log events in development for debugging
    if (GA_DEBUG) {
      console.log('📊 GA Event Fired:', event, params);
    }
  } else if (GA_DEBUG) {
    console.log('⚠️ GA Event Skipped (GA not enabled):', event, params);
  }
};

// View cart - Standard GA4 ecommerce event
export const trackViewCart = (cartItems) => {
  const totalValue = cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const items = cartItems.map(item => ({
    item_id: item.product_id,
    item_name: item.product_name,
    item_category: item.category || 'Furniture',
    price: item.price,
    quantity: item.quantity
  }));

  trackGAEvent('view_cart', {
    currency: 'INR',
    value: totalValue,
    items: items
  });
};

// Remove from cart - Standard GA4 ecommerce event
export const trackRemoveFromCart = (product, quantity = 1) => {
  trackGAEvent('remove_from_cart', {
    currency: 'INR',
    value: product.price * quantity,
    items: [{
      item_id: product.id,
      item_name: product.name,
      item_category: product.category || 'Furniture',
      price: product.price,
      quantity: quantity
    }]
  });
};

export const initMetaPixel = () => {
  if (isLikelyBotUserAgent()) return;
  if (!isPixelEnabled() || window.fbq) return;

  (function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = "2.0";
    n.queue = [];
    t = b.createElement(e);
    t.async = true;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(
    window,
    document,
    "script",
    "https://connect.facebook.net/en_US/fbevents.js"
  );

  window.fbq("init", "3905172919786438");
  window.fbq("track", "PageView");
  window.ZBPixel = {
    listingView: function () {
      window.fbq("track", "ViewContent", {
        content_name: "product_listing",
        content_category: "listing",
        content_ids: ["listing_all"],
        content_type: "product_group",
      });
    },

    productView: function (product_name, product_id) {
      window.fbq("track", "ViewContent", {
        content_name: product_name,
        content_ids: [String(product_id)],
        content_type: "product",
      });
    },

    registrationComplete: function () {
      window.fbq("track", "CompleteRegistration", {
        content_name: "price_unlock",
      });
    },

    addToCart: function (product_name, product_id, cart_value) {
      window.fbq("track", "AddToCart", {
        content_name: product_name,
        content_ids: [String(product_id)],
        content_type: "product",
        value: Number(cart_value),
        currency: "INR",
        num_items: 1,
      });
    },

    initiateCheckout: function (cart_value) {
      window.fbq("track", "InitiateCheckout", {
        value: Number(cart_value),
        currency: "INR",
      });
    },
  };
};

export const trackMetaEvent = (event) => {
  isPixelEnabled() && window.fbq && window.fbq('trackCustom', event);
};

const callZbPixel = (fn, ...args) => {
  if (!isPixelEnabled() || typeof window?.ZBPixel?.[fn] !== "function") return;
  window.ZBPixel[fn](...args);
};

export const trackZbListingView = () => {
  callZbPixel("listingView");
};

export const trackZbProductView = (productName, productId) => {
  if (productId == null || productName == null) return;
  callZbPixel("productView", productName, productId);
};

export const trackZbRegistrationComplete = () => {
  callZbPixel("registrationComplete");
};

export const trackZbAddToCart = (productName, productId, cartValue) => {
  if (productId == null || productName == null) return;
  callZbPixel("addToCart", productName, productId, Number(cartValue) || 0);
};

export const trackZbInitiateCheckout = (cartValue) => {
  callZbPixel("initiateCheckout", Number(cartValue) || 0);
};
